import React from 'react';
import { Chip, type ChipProps } from '@mui/material';

/** Gio Super Admin status colors — soft fills matching portal teal/slate theme. */
export const SA_STATUS_SX: Record<
  string,
  { bgcolor: string; color: string; borderColor: string }
> = {
  active: {
    bgcolor: 'rgba(0, 107, 130, 0.10)',
    color: '#006B82',
    borderColor: 'rgba(0, 107, 130, 0.22)'
  },
  inactive: {
    bgcolor: 'rgba(244, 63, 94, 0.08)',
    color: '#be123c',
    borderColor: 'rgba(244, 63, 94, 0.2)'
  },
  pending: {
    bgcolor: 'rgba(217, 119, 6, 0.10)',
    color: '#b45309',
    borderColor: 'rgba(217, 119, 6, 0.22)'
  },
  default: {
    bgcolor: 'var(--color-background-tertiary)',
    color: 'var(--color-text-secondary)',
    borderColor: 'var(--color-border-primary)'
  }
};

/** Order-status chips: map common statuses onto the same palette. */
const ORDER_STATUS_MAP: Record<string, keyof typeof SA_STATUS_SX> = {
  active: 'active',
  delivered: 'active',
  paid: 'active',
  invoice_paid: 'active',
  bought: 'active',
  sent: 'active',
  inactive: 'inactive',
  canceled: 'inactive',
  cancelled: 'inactive',
  out_of_stock: 'inactive',
  pending: 'pending',
  unpaid: 'pending',
  invoice_sent: 'pending'
};

export function resolveSaStatusKey(status: string): keyof typeof SA_STATUS_SX {
  const key = status?.toLowerCase?.() ?? '';
  if (key in SA_STATUS_SX) return key as keyof typeof SA_STATUS_SX;
  if (key in ORDER_STATUS_MAP) return ORDER_STATUS_MAP[key];
  return 'default';
}

type SaStatusChipProps = {
  status: string;
  label?: string;
  size?: ChipProps['size'];
};

/** Themed status chip for Super Admin tables (no MUI success/error greens). */
export const SaStatusChip: React.FC<SaStatusChipProps> = ({
  status,
  label,
  size = 'small'
}) => {
  const tone = SA_STATUS_SX[resolveSaStatusKey(status)];
  return (
    <Chip
      size={size}
      label={label ?? status.replace(/_/g, ' ')}
      variant="outlined"
      sx={{
        height: 24,
        width: 96,
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'capitalize',
        bgcolor: tone.bgcolor,
        color: tone.color,
        borderColor: tone.borderColor,
        '& .MuiChip-label': {
          px: 1,
          width: '100%',
          textAlign: 'center',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      }}
    />
  );
};

/** Neutral / brand chip (e.g. Self badge, network type). */
export const SaNeutralChip: React.FC<{
  label: string;
  size?: ChipProps['size'];
  /** Fixed width so chips in a column align (e.g. In/Out of network). */
  width?: number;
}> = ({ label, size = 'small', width }) => (
  <Chip
    size={size}
    label={label}
    variant="outlined"
    sx={{
      height: width ? 24 : 20,
      width: width || 'auto',
      fontSize: width ? 12 : 11,
      fontWeight: width ? 600 : 500,
      bgcolor: SA_STATUS_SX.default.bgcolor,
      color: SA_STATUS_SX.default.color,
      borderColor: SA_STATUS_SX.default.borderColor,
      '& .MuiChip-label': {
        px: width ? 1 : 0.75,
        ...(width
          ? {
              width: '100%',
              textAlign: 'center',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          : {})
      }
    }}
  />
);
